import React, { Fragment } from 'react';
import { Flex, Typography, Spin } from 'antd';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import Ninja from '@ninja';
import ActiveDot from 'components/charts/components/ActiveDot';
import { integrationMetricList } from 'modules/DashboardModule/charts/components/IntegrationMetricList';

const { Text } = Typography;

const AdsetAreaChart = ({
  dataKey,
  syncId = 'inner',
  data,
  total,
  name,
  color,
  height = 120,
  chartHeight = 90,
  loading = false,
  chartType = 'area',
}) => {
  const { valueFormatter, formatter } = integrationMetricList[dataKey] || {};
  const ValueFormatter = valueFormatter || Fragment;
  const FormatterComponent = formatter || Fragment;

  const renderChart = () => {
    if (chartType === 'bar') {
      return (
        <BarChart data={data} syncId={syncId} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`${color}-bar`} x1='0' y1='1' x2='0' y2='0'>
              <stop offset='0%' stopColor={color} stopOpacity={0.6} />
              <stop offset='90%' stopColor={color} stopOpacity={0.9} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke={Ninja.colors.xlight} />

          <Tooltip
            cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            wrapperStyle={{ borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
            content={({ active, payload }) =>
              active && payload?.length ? (
                <div className='bg-white rounded-md px-2 py-1'>
                  <div className='grid gap-1'>
                    <span className='font-medium text-center text-xs text-color-dark-text'>
                      {new Date(payload[0].payload?.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                    <Flex gap='middle' wrap align='center'>
                      <Typography className='font-normal text-center text-sm'>{name}:</Typography>
                      {payload.map((entry, index) => (
                        <Text className='font-medium text-center text-sm' key={index}>
                          {valueFormatter ? (
                            <ValueFormatter value={entry?.value} />
                          ) : formatter ? (
                            <FormatterComponent row={entry.payload} fontWeight='semibold' />
                          ) : (
                            entry.value
                          )}
                        </Text>
                      ))}
                    </Flex>
                  </div>
                </div>
              ) : null
            }
          />

          <Bar
            dataKey={dataKey}
            fill={`url(#${color}-bar)`}
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
          />
        </BarChart>
      );
    }

    return (
      <AreaChart data={data} syncId={syncId} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={color} x1='0' y1='1' x2='0' y2='0'>
            <stop offset='0%' stopColor={color} stopOpacity={0.01} />
            <stop offset='90%' stopColor={color} stopOpacity={0.3} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke={Ninja.colors.xlight} />

        <Tooltip
          cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
          wrapperStyle={{ borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <div className='bg-white rounded-md px-2 py-1'>
                <div className='grid gap-1'>
                  <span className='font-medium text-center text-xs text-color-dark-text'>
                    {new Date(payload[0].payload?.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <Flex gap='middle' wrap align='center'>
                    <Typography className='font-normal text-center text-sm'>{name}:</Typography>
                    {payload.map((entry, index) => (
                      <Text className='font-medium text-center text-sm' key={index}>
                        {valueFormatter ? (
                          <ValueFormatter value={entry?.value} />
                        ) : formatter ? (
                          <FormatterComponent row={entry.payload} fontWeight='semibold' />
                        ) : (
                          entry.value
                        )}
                      </Text>
                    ))}
                  </Flex>
                </div>
              </div>
            ) : null
          }
        />
        <Area
          type='linear'
          dataKey={dataKey}
          stroke={color}
          strokeWidth={1.3}
          fill={`url(#${color})`}
          activeDot={<ActiveDot radius={5} />}
        />
      </AreaChart>
    );
  };

  return (
    <div
      size='small'
      className='w-full rounded-lg'
      style={{
        height: height,
        position: 'relative',
      }}
    >
      {loading ? (
        <Flex align='center' justify='center' className='h-full'>
          <Spin size='small' />
        </Flex>
      ) : (
        <>
          {name && total && (
            <Flex align='center' justify='space-between' className='mb-1'>
              <Typography className='!text-xs font-medium text-color-dark-text'>{name}</Typography>
              <Typography className='!text-base font-semibold'>{total}</Typography>
            </Flex>
          )}

          {!data?.length ? (
            <Typography className='text-xs text-gray-400 flex justify-center mt-2 h-fit'>
              No data found
            </Typography>
          ) : (
            <ResponsiveContainer width='100%' height={chartHeight}>
              {renderChart()}
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
};

export default AdsetAreaChart;
